import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import { ApiProvider } from "./api/ApiContext.jsx";
import { panelApi } from "./api/index.js";
import { AuthProvider, useAuth } from "./auth/AuthContext.jsx";
import AppShell from "./layout/AppShell.jsx";
import { PublicOnly, RequireAdmin, RequireAuth } from "./routes/guards.jsx";
import { BootLoading } from "./ui/Feedback.jsx";
import { NotFound } from "./components/ErrorStates.jsx";

import LoginScreen from "./screens/LoginScreen.jsx";
import SignupScreen from "./screens/SignupScreen.jsx";
import TwoFactorScreen from "./screens/TwoFactorScreen.jsx";
import PasswordResetScreen from "./screens/PasswordResetScreen.jsx";
import ResetPasswordConfirmScreen from "./screens/ResetPasswordConfirmScreen.jsx";
import DashboardScreen from "./screens/DashboardScreen.jsx";
import ProjectFormScreen from "./screens/ProjectFormScreen.jsx";
import ProjectScreen from "./screens/ProjectScreen.jsx";
import ProjectSettingsScreen from "./screens/ProjectSettingsScreen.jsx";
import FeedScreen from "./screens/FeedScreen.jsx";
import BillingScreen from "./screens/BillingScreen.jsx";
import PlanPeriodScreen from "./screens/PlanPeriodScreen.jsx";
import NotificationsScreen from "./screens/NotificationsScreen.jsx";
import AccountScreen from "./screens/AccountScreen.jsx";
import HelpScreen from "./screens/HelpScreen.jsx";
import UpgradeScreen from "./screens/UpgradeScreen.jsx";
import AdminScreen from "./screens/AdminScreen.jsx";
import AdminReliabilityScreen from "./screens/AdminReliabilityScreen.jsx";

function NotFoundScreen() {
  const navigate = useNavigate();
  return <NotFound onBack={() => navigate("/")} />;
}

/** Wallet of routes; exported so tests can mount it under their own router. */
export function AppRoutes() {
  const { status } = useAuth();
  if (status === "loading") return <BootLoading />;

  return (
    <Routes>
      <Route element={<PublicOnly />}>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/signup" element={<SignupScreen />} />
        <Route path="/2fa" element={<TwoFactorScreen />} />
        <Route path="/reset-password" element={<PasswordResetScreen />} />
        <Route path="/reset-password/confirm" element={<ResetPasswordConfirmScreen />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route path="/" element={<DashboardScreen />} />
          <Route path="/projects/new" element={<ProjectFormScreen />} />
          <Route path="/projects/:projectId" element={<ProjectScreen />} />
          <Route path="/projects/:projectId/edit" element={<ProjectFormScreen />} />
          <Route path="/projects/:projectId/settings" element={<ProjectSettingsScreen />} />
          <Route path="/feeds/:feedId" element={<FeedScreen />} />
          <Route path="/billing" element={<BillingScreen />} />
          {/* Choosing a tier and choosing a period are two steps: the period
              decides which price the checkout is created for. */}
          <Route path="/billing/plan/:planId" element={<PlanPeriodScreen />} />
          <Route path="/notifications" element={<NotificationsScreen />} />
          <Route path="/account" element={<AccountScreen />} />
          <Route path="/help" element={<HelpScreen />} />
          <Route path="/upgrade" element={<UpgradeScreen />} />

          <Route element={<RequireAdmin />}>
            <Route path="/admin" element={<AdminScreen />} />
            <Route path="/admin/reliability" element={<AdminReliabilityScreen />} />
          </Route>

          <Route path="*" element={<NotFoundScreen />} />
        </Route>
      </Route>
    </Routes>
  );
}

export function AppProviders({ api = panelApi, children }) {
  return (
    <ApiProvider client={api}>
      <AuthProvider>{children}</AuthProvider>
    </ApiProvider>
  );
}

export default function App() {
  // BASE_URL is "/" by default; building with `--base=/panel/` (Caddy serves
  // the SPA under /panel/*) makes the router follow automatically.
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL || "/"}>
      <AppProviders>
        <AppRoutes />
      </AppProviders>
    </BrowserRouter>
  );
}
